import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { TasksService } from '@/services/tasks.service'

import {
	ITranscriptions,
	IUploadHighlightRequest
} from '@/types/task.interface'

import {
	getNextStartAfter,
	getRefreshBoundary,
	mergeTranscriptions,
	normalizeTranscriptions,
	replaceTranscriptionsFrom
} from '../helpers'

export const useLive = (externalTaskId?: string) => {
	const { id } = useParams()
	const taskId = externalTaskId || id

	const [transcriptionState, setTranscriptionState] =
		useState<ITranscriptions>(null)

	const isFetchingRef = useRef(false)
	const lastStartAfterRef = useRef<number | null>(null)
	const transcriptionStateRef = useRef<ITranscriptions>(null)
	transcriptionStateRef.current = transcriptionState

	const {
		data: task,
		isLoading: isTaskLoading,
		refetch: refetchTask
	} = useQuery({
		queryKey: ['getTask', taskId],
		queryFn: () => TasksService.getTask(taskId),
		enabled: !!taskId,
		refetchInterval: currentTask => {
			if (!currentTask) return false

			const shouldPoll =
				currentTask.source_type === 'listener' &&
				currentTask.status === 'in_progress' &&
				!currentTask.audio_tracks

			return shouldPoll ? 15000 : false
		}
	})

	const isLive =
		task?.status === 'in_progress' && task?.source_type === 'listener'

	useEffect(() => {
		if (!taskId) return

		let isCancelled = false

		setTranscriptionState(null)
		lastStartAfterRef.current = null

		TasksService.getTranscriptions(taskId)
			.then(data => {
				if (isCancelled || !data) return

				const normalizedData = normalizeTranscriptions(data)

				setTranscriptionState(normalizedData)
				lastStartAfterRef.current = getNextStartAfter(normalizedData)
			})
			.catch(error => {
				if (!isCancelled) {
					console.error('Failed to load live transcriptions:', error)
				}
			})

		return () => {
			isCancelled = true
		}
	}, [taskId])

	const refetchTranscriptions = useCallback(async () => {
		if (isFetchingRef.current) return
		isFetchingRef.current = true

		try {
			if (!taskId || !isLive) return

			// re-fetch a trailing window from a chunk boundary so refined
			// translations replace what is already on screen
			const boundary = getRefreshBoundary(
				transcriptionStateRef.current,
				lastStartAfterRef.current
			)
			const startAfter =
				boundary !== null
					? Math.max(0, boundary - 0.001)
					: lastStartAfterRef.current ?? undefined

			const data = await TasksService.getTranscriptions(taskId, startAfter)

			if (!data) return

			const normalizedData = normalizeTranscriptions(data)

			setTranscriptionState(prev => {
				const merged = !prev
					? normalizedData
					: boundary !== null
					? replaceTranscriptionsFrom(prev, normalizedData, boundary)
					: mergeTranscriptions(prev, normalizedData)
				const nextStartAfter = getNextStartAfter(merged)

				if (typeof nextStartAfter === 'number') {
					lastStartAfterRef.current = nextStartAfter
				}

				return merged
			})
		} finally {
			isFetchingRef.current = false
		}
	}, [taskId, isLive])

	const { mutateAsync: uploadHighlight, isLoading: isUploadHighlightLoading } =
		useMutation({
			mutationKey: ['uploadHighlight'],
			mutationFn: (data: IUploadHighlightRequest) =>
				TasksService.uploadHighlight(data)
		})

	const {
		mutateAsync: uploadHighlightsDraft,
		isLoading: isUploadHighlightsDraftLoading
	} = useMutation({
		mutationKey: ['uploadHighlightsDraft'],
		mutationFn: (data: IUploadHighlightRequest) =>
			TasksService.uploadHighlightsDraft(data)
	})

	return {
		task,
		isTaskLoading,
		refetchTask,
		transcriptions: transcriptionState,
		refetchTranscriptions,
		isTranscriptionsLoading: !transcriptionState,
		uploadHighlight,
		isUploadHighlightLoading,
		uploadHighlightsDraft,
		isUploadHighlightsDraftLoading
	}
}
